import { openUserDetail } from '@/features/gateway/utils/utils'
import {
  useIsCurrentStudentArchived,
  useCurrentSchoolId,
  useIsShadow,
} from '@/hooks'
import { reflectionStorage, store, xRbacTokenStorage } from '@/store'
import { getTheme } from '@/utils/theme'
import { DropdownButton, DropdownButtonItem, Toast } from '@seiue/ui'
import { Avatar } from 'antd'
import { useSetAtom } from 'jotai'
import { MeRerm, authApi } from 'packages/sdks-next/growp'
import { useRef } from 'react'
import { useHistory as useOriginHistory } from 'react-router-dom'
import styled from 'styled-components'

import { goAdminPageAtom } from './atoms'

type ExtraDropdownButtonItem = DropdownButtonItem & {
  hide?: boolean
}

/**
 * 全局布局头部右侧
 *
 * @param param - 参数
 * @param param.showIndex - 显示首页
 * @param param.setShowIndex - 设置显示首页
 * @param param.perms - 用户权限
 * @param param.isForeground - 是否前台
 * @param param.extraItems - 额外的菜单项
 * @returns 组件
 */
export const HeaderRight: React.FC<{
  showIndex: boolean
  setShowIndex: (value: boolean) => void
  perms: MeRerm | null
  isForeground: boolean
  extraItems?: ExtraDropdownButtonItem[]
  selectedKeys?: string[]
}> = ({
  perms,
  isForeground,
  setShowIndex,
  showIndex,
  extraItems,
  selectedKeys,
}) => {
  const { data: reflection, reload } = authApi.useLoadReflection()
  const setGoAdminPage = useSetAtom(goAdminPageAtom)
  const avatarRef = useRef<HTMLSpanElement>(null)
  const theme = getTheme()

  const isStudentArchived = useIsCurrentStudentArchived()
  const schoolId = useCurrentSchoolId()
  const historyOrigin = useOriginHistory()
  const showName = reflection?.name ? reflection.name.slice(-2) : '-'
  const isShadow = useIsShadow()

  const items: ExtraDropdownButtonItem[] = [
    {
      label: 'avatar',
      render: () => (
        <AvatorWrapper>
          <StyledAvatar
            size={48}
            src={reflection?.avator}
            ref={avatarRef}
          >
            {showName}
          </StyledAvatar>
          <div>{reflection?.name || reflection?.phone || '-'}</div>
        </AvatorWrapper>
      ),
    },
    {
      label: '',
      divider: true,
    },
    {
      label: '个人档案',
      callback: () => {
        if (!reflection) return
        openUserDetail(reflection.rid, reflection.role || '', () => {
          reload()
        })
      },
      hide: !(isForeground && !isStudentArchived && !isShadow),
    },
    {
      label: `进入${isForeground ? '后台' : '前台'}`,
      callback: () => {
        setGoAdminPage(isForeground)
        historyOrigin.push(isForeground ? '/' : '/user/gateway')
      },
      hide: !perms?.schoolBackgroundPerms?.length,
    },
    {
      label: showIndex ? '账号管理' : '回到首页',
      callback: () => {
        setShowIndex(!showIndex)
      },
      hide: !isForeground,
    },
    ...(extraItems || []),
    {
      label: '',
      divider: true,
    },
    {
      label: '退出登录',
      render: () => (
        <Logout
          onClick={() => {
            localStorage.setItem('user-preview', '')
            store.set(reflectionStorage, undefined)
            store.set(xRbacTokenStorage, undefined)
            if (!schoolId) {
              Toast.warning('未找到学校信息')
            }
            historyOrigin.push(schoolId ? `/login/${schoolId}` : '/404')
          }}
        >
          <div>退出登录</div>
        </Logout>
      ),
    },
  ].filter((item) => !item.hide)

  const handleDropdownVisible = (visible: boolean) => {
    if (!visible) return
    const eles = document.querySelectorAll<HTMLDivElement>(
      '.ant-dropdown-menu-title-content'
    )

    // 调整菜单样式
    eles.forEach((el, index) => {
      // 找出 items 里所有自定义了 render 的 index 集合
      const filterItems = items.filter((item) => !item.divider)

      const curItem = filterItems[index]
      // 自定义 render 的菜单，不需要调整样式
      if (!!curItem.render) return
      el.style.height = '48px'
      el.style.color = '#152529'
      el.style.padding = '6px 12px'
      el.style.minWidth = '124px'
    })

    // 调整头像背景色
    if (avatarRef.current) {
      avatarRef.current.style.setProperty(
        'background-color',
        theme.background._1,
        'important'
      )
    }
  }

  return (
    <DropdownButton
      onVisibleChange={(open) => {
        setTimeout(() => {
          handleDropdownVisible(open)
        }, 0)
      }}
      overlayStyle={{
        width: 200,
        backgroundColor: '#fff',
      }}
      showSelected={true}
      selectedKeys={selectedKeys}
      items={items}
      showArrow={false}
      children={
        <Avatar
          size={32}
          src={reflection?.avator}
          style={{ cursor: 'pointer', backgroundColor: theme.background._1 }}
        >
          {showName}
        </Avatar>
      }
    />
  )
}

const StyledAvatar = styled(Avatar)``

const AvatorWrapper = styled.div`
  width: 100%;
  height: 118px;
  padding: 20px 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  &:hover {
    background-color: transparent;
  }
`

const Logout = styled.div`
  height: 48px;
  padding: 12px 16px;
  color: #f54545;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.brand._5};
  }
  display: flex;
  flex-direction: row;
  justify-content: center;
`
